/**
 * Sound Mappings for Poker Reborn
 *
 * Two-layer sound system:
 * 1. SOUND_REGISTRY (from soundDiscovery.ts) - auto-groups files by base name
 * 2. SOUND_MAPPINGS - maps event sound IDs to file groups
 *
 * Naming Convention:
 *   File: {base}{N}.mp3 → Sound ID: "{base}"
 *   Example: deal1.mp3, deal2.mp3 → Sound ID "deal"
 *
 * @see docs/systems/SOUND_EVENTS.md - Sound system documentation
 */

import type { SoundEffect } from '@/types/ui';
import { SOUND_REGISTRY, getRandomVariant } from './soundDiscovery';

// =============================================================================
// SOUND MAPPINGS
// =============================================================================

/**
 * Maps sound IDs to arrays of file variants.
 *
 * Most IDs come directly from SOUND_REGISTRY (auto-discovered).
 * Additional aliases are defined for backwards compatibility or semantic clarity.
 */
export const SOUND_MAPPINGS: Record<string, SoundEffect[]> = {
  // ---------------------------------------------------------------------------
  // Auto-discovered sounds (from SOUND_REGISTRY)
  // Use base file name as Sound ID
  // ---------------------------------------------------------------------------
  ...SOUND_REGISTRY,

  // ---------------------------------------------------------------------------
  // Aliases (for backwards compatibility or semantic naming)
  // These map descriptive names to auto-discovered sound groups
  // ---------------------------------------------------------------------------

  /** chips_collect → chips_end (semantic alias) */
  chips_collect: SOUND_REGISTRY['chips_end'] ?? ['chips_end'],

  /** chips_return → chip sounds (reuse chip sounds) */
  chips_return: SOUND_REGISTRY['chip'] ?? ['chip1', 'chip2', 'chip3', 'chip4'],

  /** win → chips_win (semantic alias) */
  win: SOUND_REGISTRY['chips_win'] ?? ['chips_win'],

  /** big_win → applause (semantic alias) */
  big_win: SOUND_REGISTRY['applause'] ?? ['applause'],

  /** pot_award_hero → chips_win (hero wins pot) */
  pot_award_hero: SOUND_REGISTRY['chips_win'] ?? ['chips_win'],

  /** pot_award_opponent → chips_end (opponent wins pot) */
  pot_award_opponent: SOUND_REGISTRY['chips_end'] ?? ['chips_end'],
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get a random sound variant for a sound ID.
 *
 * @param soundId - Sound ID (e.g., 'deal', 'flop', 'chip')
 * @returns Random actual sound file ID, or undefined if not found
 */
export function getRandomSound(soundId: string): SoundEffect | undefined {
  // First check SOUND_MAPPINGS (includes aliases)
  const mappedSounds = SOUND_MAPPINGS[soundId];
  if (mappedSounds && mappedSounds.length > 0) {
    const index = Math.floor(Math.random() * mappedSounds.length);
    return mappedSounds[index];
  }

  // Fallback to direct registry lookup
  return getRandomVariant(soundId);
}

/**
 * Check if a sound ID exists (either mapped or in registry).
 *
 * @param soundId - Sound ID to check
 * @returns true if this sound ID is available
 */
export function isMappedSound(soundId: string): boolean {
  return soundId in SOUND_MAPPINGS || soundId in SOUND_REGISTRY;
}
